import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Play,
  Building2,
  Users,
  Wrench,
  CheckCircle2,
  Search,
  MapPin,
  Star,
} from 'lucide-react';
import { providerService } from '../services/providerService';
import IconLogo from '../assets/Icon_logo.svg';
import DemoModal from '../components/DemoModal';
import LandingHeader from '../components/LandingHeader';
import { useLanguage } from '../contexts/LanguageContext';

const LandingPage = () => {
  const { t } = useLanguage();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [showDemoModal, setShowDemoModal] = useState(false);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const allProviders = await providerService.getAvailableProviders();
        setProviders(allProviders);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching providers:', error);
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = selectedSpecialty === 'all' || provider.specialty === selectedSpecialty;
    return matchesSearch && matchesSpecialty;
  });

  const sortedProviders = [...filteredProviders].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'name':
        return (a.displayName || '').localeCompare(b.displayName || '');
      case 'location':
        return (a.location || '').localeCompare(b.location || '');
      default:
        return 0;
    }
  });

  const specialties = [
    'plumbing', 'electrical', 'hvac', 'carpentry', 'painting', 
    'cleaning', 'landscaping', 'roofing', 'general'
  ];

  const getSpecialtyColor = (specialty) => {
    switch (specialty) {
      case 'plumbing': return 'bg-blue-100 text-blue-800';
      case 'electrical': return 'bg-yellow-100 text-yellow-800';
      case 'hvac': return 'bg-purple-100 text-purple-800';
      case 'carpentry': return 'bg-orange-100 text-orange-800';
      case 'painting': return 'bg-pink-100 text-pink-800';
      case 'cleaning': return 'bg-cyan-100 text-cyan-800';
      case 'landscaping': return 'bg-green-100 text-green-800';
      case 'roofing': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSpecialtyIcon = (specialty) => {
    switch (specialty) {
      case 'plumbing':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'electrical':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'hvac':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'carpentry':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'painting':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        );
      case 'cleaning':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'landscaping':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'roofing':
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
    }
  };



  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Marquee strip – subtle */}
      <div className="border-b border-slate-100 bg-slate-50/90 py-2.5 overflow-hidden">
        <div className="flex animate-scroll whitespace-nowrap text-xs font-medium text-slate-600">
          <span className="mx-6">{t('marquee.earlyAccess')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.noPayment')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.prioritySupport')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.exclusiveFeatures')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.verifiedNetwork')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.analytics')}</span>
          <span className="mx-6">{t('marquee.customerSuccess')}</span>
          <span className="mx-6">{t('marquee.earlyAccess')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.noPayment')}</span>
          <span className="text-slate-300">·</span>
          <span className="mx-6">{t('marquee.prioritySupport')}</span>
        </div>
      </div>

      {/* Hero – modern, distinctive */}
      <section className="relative overflow-hidden border-b border-slate-200/60 bg-gradient-to-b from-slate-50 to-white py-20 sm:py-28 lg:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(148,163,184,0.12),transparent)]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-600 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t('hero.badge')}
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {t('hero.title')}
            <span className="mt-2 block text-slate-600">
              {t('hero.subtitle')}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600 sm:text-xl">
            {t('hero.description')}
          </p>
          <p className="mt-2 text-base font-medium text-slate-500">
            {t('hero.programSubtitle')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800 hover:shadow-xl sm:w-auto"
            >
              {t('hero.earlyAccessBtn')}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </Link>
            <button
              type="button"
              onClick={() => setShowDemoModal(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-8 py-4 text-base font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50 sm:w-auto"
            >
              <Play className="h-5 w-5" strokeWidth={2} />
              {t('hero.watchDemoBtn')}
            </button>
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-slate-400" strokeWidth={1.75} />
              {t('hero.trustIndicators.noPayment')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-slate-400" strokeWidth={1.75} />
              {t('hero.trustIndicators.prioritySupport')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-slate-400" strokeWidth={1.75} />
              {t('hero.trustIndicators.limitedAvailability')}
            </span>
          </div>
        </div>
      </section>

      {/* Early Access strip */}
      <section className="border-b border-emerald-700/50 bg-emerald-600 py-4">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-4 px-4 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
            <span className="font-semibold text-white">{t('earlyAccessBanner.title')}</span>
          </div>
          <p className="text-center text-sm text-emerald-50 sm:text-left">
            {t('earlyAccessBanner.description')}
          </p>
          <Link
            to="/register"
            className="shrink-0 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
          >
            {t('earlyAccessBanner.joinNow')}
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 sm:py-20 border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            <div className="text-center">
              <div className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">1000+</div>
              <div className="mt-1 text-sm text-slate-600">{t('statistics.propertyOwners')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">500+</div>
              <div className="mt-1 text-sm text-slate-600">{t('statistics.serviceProviders')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">2000+</div>
              <div className="mt-1 text-sm text-slate-600">{t('statistics.propertiesManaged')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">99.9%</div>
              <div className="mt-1 text-sm text-slate-600">{t('statistics.uptime')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28 bg-slate-50/80">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t('features.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              {t('features.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-slate-300 hover:shadow-md">
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition group-hover:bg-slate-100">
                <Building2 className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">{t('features.features.0.title')}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t('features.features.0.description')}</p>
            </div>
            <div className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-slate-300 hover:shadow-md">
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition group-hover:bg-slate-100">
                <Wrench className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">{t('features.features.1.title')}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t('features.features.1.description')}</p>
            </div>
            <div className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-slate-300 hover:shadow-md">
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition group-hover:bg-slate-100">
                <Users className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900">{t('features.features.2.title')}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t('features.features.2.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Providers */}
      <section id="providers" className="py-20 sm:py-28 border-t border-slate-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t('serviceProviders.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              {t('serviceProviders.subtitle')}
            </p>
          </div>
          <div className="mb-10 rounded-xl border border-slate-200 bg-slate-50/50 p-5 shadow-sm">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
                <input
                  type="text"
                  placeholder={t('serviceProviders.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-200 focus:ring-offset-0"
                />
              </div>
              <div>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="all">{t('serviceProviders.allSpecialties')}</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>
                      {t(`serviceProviders.specialties.${specialty}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="rating">{t('serviceProviders.sortByRating')}</option>
                  <option value="name">{t('serviceProviders.sortByName')}</option>
                  <option value="location">{t('serviceProviders.sortByLocation')}</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-600" />
              <p className="mt-4 text-sm text-slate-600">{t('serviceProviders.loading')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sortedProviders.map((provider) => (
                <div
                  key={provider.id}
                  className="relative rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-slate-300 hover:shadow-md"
                >
                  <div className="absolute right-4 top-4 opacity-50">
                    {getSpecialtyIcon(provider.specialty)}
                  </div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">{provider.displayName}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                        {t(`serviceProviders.specialties.${provider.specialty}`)}
                      </span>
                      {provider.rating && (
                        <span className="flex items-center gap-1 text-sm text-slate-600">
                          <Star className="h-4 w-4 fill-slate-200 text-slate-500" strokeWidth={1.5} />
                          {provider.rating}
                        </span>
                      )}
                    </div>
                  </div>
                  {provider.description && (
                    <p className="mb-4 line-clamp-2 text-sm text-slate-600">{provider.description}</p>
                  )}
                  {provider.location && (
                    <div className="mb-4 flex items-center gap-2 text-sm text-slate-500">
                      <MapPin className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      <span className="truncate">{provider.location}</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Link
                      to="/register"
                      className="flex-1 rounded-lg bg-slate-900 py-2.5 text-center text-sm font-medium text-white transition hover:bg-slate-800"
                    >
                      {t('serviceProviders.joinPlatform')}
                    </Link>
                    <Link
                      to="/register"
                      className="flex-1 rounded-lg border border-slate-200 py-2.5 text-center text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      {t('serviceProviders.learnMore')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && sortedProviders.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                <Search className="h-8 w-8 text-slate-400" strokeWidth={1.25} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{t('serviceProviders.noResults')}</h3>
              <p className="mt-2 text-sm text-slate-600">{t('serviceProviders.noResultsDescription')}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-amber-200 bg-amber-400 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-amber-950 sm:text-4xl">
            {t('cta.title')}
          </h2>
          <p className="mt-5 text-lg text-amber-900/90">
            {t('cta.description')}
          </p>
          <Link
            to="/register"
            className="mt-10 inline-flex items-center gap-2 rounded-xl bg-amber-950 px-8 py-4 text-base font-semibold text-amber-50 shadow-lg transition hover:bg-amber-900"
          >
            {t('cta.button')}
            <ArrowRight className="h-5 w-5" strokeWidth={2} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-14 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <img className="h-10 w-auto opacity-90" src={IconLogo} alt="EasyProperty" />
              <p className="mt-4 text-sm text-slate-400">{t('footer.description')}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.platform')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><Link to="/register" className="transition hover:text-white">{t('footer.signUp')}</Link></li>
                <li><Link to="/login" className="transition hover:text-white">{t('footer.signIn')}</Link></li>
                <li><Link to="/providers" className="transition hover:text-white">{t('footer.serviceProviders')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.support')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.helpCenter')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.contactUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.privacyPolicy')}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.connect')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.aboutUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.blog')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.careers')}</button></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      <DemoModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />
    </div>
  );
};

export default LandingPage; 